import { EyeIcon, EyeSlashIcon, PencilIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../contexts/ThemeContext';
import clsx from 'clsx';
import apiService, { Employee, EmployeeData } from '../services/api';

const EmployeeManagementPage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { theme } = useTheme();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Edit employee states
  const [showEditForm, setShowEditForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [editEmployee, setEditEmployee] = useState<Partial<EmployeeData>>({});
  
  usePageTitle('Employee Management');

  const [newEmployee, setNewEmployee] = useState<EmployeeData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'employee',
    bio: '',
  });

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const employeeList = await apiService.getEmployees();
      setEmployees(employeeList);
    } catch (error) {
      console.error('Failed to load employees:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load employees',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof EmployeeData, value: string) => {
    setNewEmployee(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAdding(true);
    setMessage(null);

    try {
      await apiService.addEmployee(newEmployee);
      setMessage({
        type: 'success',
        text: 'Employee added successfully!',
      });
      
      // Reset form and reload employees
      setNewEmployee({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: 'employee',
        bio: '',
      });
      setShowAddForm(false);
      loadEmployees();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to add employee',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggleStatus = async (employeeId: number, currentStatus: boolean) => {
    try {
      const action = currentStatus ? 'deactivate' : 'activate';
      await apiService.toggleEmployeeStatus(employeeId, action);
      
      setMessage({
        type: 'success',
        text: `Employee ${action}d successfully!`,
      });
      
      loadEmployees();
      
      // Refresh user data if the current user's status was changed
      if (user && user.id === employeeId) {
        await refreshUser();
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to update employee status',
      });
    }
  };

  const handleEditEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setEditEmployee({
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email,
      phone: employee.phone || '',
      role: employee.membership_type,
      bio: employee.bio || ''
    });
    setShowEditForm(true);
  };

  const handleEditInputChange = (field: keyof EmployeeData, value: string) => {
    setEditEmployee(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee) return;

    setIsEditing(true);
    setMessage(null);

    try {
      await apiService.updateEmployee(selectedEmployee.id, editEmployee);
      setMessage({
        type: 'success',
        text: 'Employee updated successfully!',
      });
      
      setShowEditForm(false);
      setSelectedEmployee(null);
      setEditEmployee({});
      loadEmployees();
      
      // Refresh user data to update navigation and permissions
      await refreshUser();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to update employee',
      });
    } finally {
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setSelectedEmployee(null);
    setEditEmployee({});
  };

  const roleOptions = [
    { 
      value: 'owner', 
      label: 'Owner', 
      description: 'Full access to all features including billing & payments',
      permissions: ['all'],
      color: 'text-purple-400'
    },
    { 
      value: 'employee', 
      label: 'Employee', 
      description: 'Access to all features except billing & payments',
      permissions: ['view_schedule', 'view_members', 'manage_classes', 'manage_workouts', 'view_reports', 'manage_employees'],
      color: 'text-blue-400'
    },
    { 
      value: 'viewer', 
      label: 'Viewer', 
      description: 'View-only access to all pages except billing & payments',
      permissions: ['view_schedule', 'view_members', 'view_classes', 'view_workouts', 'view_reports'],
      color: 'text-green-400'
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className={clsx(
          "text-4xl font-bold bg-clip-text text-transparent",
          theme === 'dark'
            ? "bg-gradient-to-r from-white to-gray-300"
            : "bg-gradient-to-r from-gray-900 to-gray-700"
        )}>
          Employee Management
        </h1>
        <p className={clsx(
          "mt-3 text-lg transition-colors",
          theme === 'dark' ? "text-gray-300" : "text-gray-600"
        )}>
          Manage your gym's employees and staff members
        </p>
      </div>

      {message && (
        <div className={clsx(
          "rounded-xl p-4 backdrop-blur-sm border",
          message.type === 'success' 
            ? clsx(
                "bg-green-500/20 border-green-400/30",
                theme === 'dark' ? "text-green-200" : "text-green-700"
              )
            : clsx(
                "bg-red-500/20 border-red-400/30",
                theme === 'dark' ? "text-red-200" : "text-red-700"
              )
        )}>
          {message.text}
        </div>
      )}

      {/* Main Content Card */}
      <div className={clsx(
        "backdrop-blur-lg rounded-2xl shadow-2xl border p-8 transition-colors",
        theme === 'dark'
          ? "bg-white/10 border-white/20"
          : "bg-white/80 border-gray-200/50"
      )}>
        <div className="flex justify-between items-center mb-6">
          <h2 className={clsx(
            "text-2xl font-bold transition-colors",
            theme === 'dark' ? "text-white" : "text-gray-900"
          )}>Employee Management</h2>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <PlusIcon className="h-4 w-4 mr-2 inline" />
            Add Employee
          </button>
        </div>

      {/* Add Employee Form */}
      {showAddForm && (
        <div className="mb-8">
          <h3 className={clsx(
            "text-xl font-bold mb-6 transition-colors",
            theme === 'dark' ? "text-white" : "text-gray-900"
          )}>Add New Employee</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>First Name *</label>
                <input
                  type="text"
                  required
                  value={newEmployee.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className={clsx(
                    "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                  )}
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>Last Name *</label>
                <input
                  type="text"
                  required
                  value={newEmployee.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className={clsx(
                    "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                  )}
                  placeholder="Enter last name"
                />
              </div>
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>Email *</label>
                <input
                  type="email"
                  required
                  value={newEmployee.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={clsx(
                    "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                  )}
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>Phone</label>
                <input
                  type="tel"
                  value={newEmployee.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={clsx(
                    "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                  )}
                  placeholder="Enter phone number (optional)"
                />
              </div>
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>Role *</label>
                <select
                  required
                  value={newEmployee.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className={clsx(
                    "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white focus:bg-white/20"
                      : "bg-white border-gray-300 text-gray-900 focus:bg-white"
                  )}
                >
                  {roleOptions.map((role) => (
                    <option key={role.value} value={role.value} className="bg-gray-800">
                      {role.label}
                    </option>
                  ))}
                </select>
                
                {/* Role Description */}
                <div className={clsx(
                  "mt-2 p-3 rounded-lg border transition-colors",
                  theme === 'dark'
                    ? "bg-white/5 border-white/10"
                    : "bg-gray-50 border-gray-200"
                )}>
                  <div className="flex items-center mb-1">
                    <span className={clsx(
                      "font-medium transition-colors",
                      roleOptions.find(r => r.value === newEmployee.role)?.color || (theme === 'dark' ? 'text-gray-300' : 'text-gray-700')
                    )}>
                      {roleOptions.find(r => r.value === newEmployee.role)?.label || 'Select a role'}
                    </span>
                  </div>
                  <p className={clsx(
                    "text-sm transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-600"
                  )}>
                    {roleOptions.find(r => r.value === newEmployee.role)?.description || 'Please select a role to see details'}
                  </p>
                  
                  {/* Permissions List */}
                  {newEmployee.role && (
                    <div className="mt-2">
                      <p className={clsx(
                        "text-xs mb-1 transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-500"
                      )}>Permissions:</p>
                      <div className="flex flex-wrap gap-1">
                        {roleOptions.find(r => r.value === newEmployee.role)?.permissions.map((permission, index) => (
                          <span 
                            key={index}
                            className={clsx(
                              "text-xs bg-indigo-500/20 px-2 py-1 rounded",
                              theme === 'dark' ? "text-indigo-300" : "text-indigo-700"
                            )}
                          >
                            {permission.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div>
              <label className={clsx(
                "block text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-700"
              )}>Bio</label>
              <textarea
                value={newEmployee.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                rows={3}
                className={clsx(
                  "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                  theme === 'dark'
                    ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                )}
                placeholder="Enter employee bio (optional)"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className={clsx(
                  "px-4 py-2 backdrop-blur-sm border rounded-xl transition-all duration-200",
                  theme === 'dark'
                    ? "bg-white/10 border-white/20 text-white hover:bg-white/20"
                    : "bg-white border-gray-300 text-gray-900 hover:bg-gray-50"
                )}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isAdding}
                className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isAdding ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Adding...
                  </div>
                ) : (
                  'Add Employee'
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Employees List */}
      <div className="mt-8">
        <h3 className={clsx(
          "text-xl font-bold mb-6 transition-colors",
          theme === 'dark' ? "text-white" : "text-gray-900"
        )}>Current Employees</h3>
        <div className="overflow-x-auto">
          <table className={clsx(
            "min-w-full divide-y transition-colors",
            theme === 'dark' ? "divide-white/10" : "divide-gray-200"
          )}>
            <thead className={clsx(
              "transition-colors",
              theme === 'dark' ? "bg-white/5" : "bg-gray-50"
            )}>
              <tr>
                <th className={clsx(
                  "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>
                  Employee
                </th>
                <th className={clsx(
                  "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>
                  Role
                </th>
                <th className={clsx(
                  "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>
                  Status
                </th>
                <th className={clsx(
                  "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>
                  Last Login
                </th>
                <th className={clsx(
                  "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={clsx(
              "bg-transparent divide-y transition-colors",
              theme === 'dark' ? "divide-white/10" : "divide-gray-200"
            )}>
              {employees.map((employee) => (
                <tr key={employee.id} className={clsx(
                  "transition-all duration-200",
                  theme === 'dark' ? "hover:bg-white/5" : "hover:bg-gray-50"
                )}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {employee.avatar_url ? (
                          <img
                            className={clsx(
                              "h-10 w-10 rounded-full border-2 transition-colors",
                              theme === 'dark' ? "border-white/20" : "border-gray-200"
                            )}
                            src={employee.avatar_url}
                            alt=""
                          />
                        ) : (
                          <div className={clsx(
                            "h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center border-2 transition-colors",
                            theme === 'dark' ? "border-white/20" : "border-gray-200"
                          )}>
                            <span className="text-sm font-medium text-white">
                              {employee.first_name[0]}{employee.last_name[0]}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className={clsx(
                          "text-sm font-medium transition-colors",
                          theme === 'dark' ? "text-white" : "text-gray-900"
                        )}>
                          {employee.first_name} {employee.last_name}
                        </div>
                        <div className={clsx(
                          "text-sm transition-colors",
                          theme === 'dark' ? "text-gray-300" : "text-gray-600"
                        )}>{employee.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={clsx(
                      "inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-indigo-500/20 border border-indigo-400/30",
                      theme === 'dark' ? "text-indigo-200" : "text-indigo-700"
                    )}>
                      {employee.membership_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={clsx(
                      "inline-flex px-3 py-1 text-xs font-semibold rounded-full border",
                      employee.is_active
                        ? clsx(
                            "bg-green-500/20 border-green-400/30",
                            theme === 'dark' ? "text-green-200" : "text-green-700"
                          )
                        : clsx(
                            "bg-red-500/20 border-red-400/30",
                            theme === 'dark' ? "text-red-200" : "text-red-700"
                          )
                    )}>
                      {employee.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className={clsx(
                    "px-6 py-4 whitespace-nowrap text-sm transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-600"
                  )}>
                    {employee.last_login
                      ? new Date(employee.last_login).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditEmployee(employee)}
                        className={clsx(
                          "inline-flex items-center px-3 py-1 border text-xs leading-4 font-medium rounded-lg transition-all duration-200 bg-blue-500/20 border-blue-400/30 hover:bg-blue-500/30",
                          theme === 'dark' ? "text-blue-200" : "text-blue-700"
                        )}
                      >
                        <PencilIcon className="h-3 w-3 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleStatus(employee.id, employee.is_active)}
                        className={clsx(
                          "inline-flex items-center px-3 py-1 border text-xs leading-4 font-medium rounded-lg transition-all duration-200",
                          employee.is_active
                            ? clsx(
                                "bg-red-500/20 border-red-400/30 hover:bg-red-500/30",
                                theme === 'dark' ? "text-red-200" : "text-red-700"
                              )
                            : clsx(
                                "bg-green-500/20 border-green-400/30 hover:bg-green-500/30",
                                theme === 'dark' ? "text-green-200" : "text-green-700"
                              )
                        )}
                      >
                        {employee.is_active ? (
                          <>
                            <EyeSlashIcon className="h-3 w-3 mr-1" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <EyeIcon className="h-3 w-3 mr-1" />
                            Activate
                          </>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      {/* Edit Employee Modal */}
      {showEditForm && selectedEmployee && (
        <div className="fixed inset-0 lg:left-64 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm" onClick={handleCancelEdit} />
            <div className={clsx(
              "relative w-full max-w-2xl backdrop-blur-lg border rounded-2xl shadow-2xl transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white border-gray-200"
            )}>
              <div className={clsx(
                "flex items-center justify-between p-6 border-b transition-colors",
                theme === 'dark' ? "border-white/20" : "border-gray-200"
              )}>
                <h3 className={clsx(
                  "text-xl font-semibold transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>
                  Edit Employee: {selectedEmployee.first_name} {selectedEmployee.last_name}
                </h3>
                <button
                  onClick={handleCancelEdit}
                  className={clsx(
                    "transition-colors",
                    theme === 'dark' ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
                  )}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleEditSubmit} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={clsx(
                      "block text-sm font-medium mb-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-700"
                    )}>First Name *</label>
                    <input
                      type="text"
                      required
                      value={editEmployee.first_name || ''}
                      onChange={(e) => handleEditInputChange('first_name', e.target.value)}
                      className={clsx(
                        "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                      )}
                      placeholder="Enter first name"
                    />
                  </div>
                  <div>
                    <label className={clsx(
                      "block text-sm font-medium mb-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-700"
                    )}>Last Name *</label>
                    <input
                      type="text"
                      required
                      value={editEmployee.last_name || ''}
                      onChange={(e) => handleEditInputChange('last_name', e.target.value)}
                      className={clsx(
                        "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                      )}
                      placeholder="Enter last name"
                    />
                  </div>
                  <div>
                    <label className={clsx(
                      "block text-sm font-medium mb-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-700"
                    )}>Email *</label>
                    <input
                      type="email"
                      required
                      value={editEmployee.email || ''}
                      onChange={(e) => handleEditInputChange('email', e.target.value)}
                      className={clsx(
                        "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                      )}
                      placeholder="Enter email address"
                    />
                  </div>
                  <div>
                    <label className={clsx(
                      "block text-sm font-medium mb-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-700"
                    )}>Phone</label>
                    <input
                      type="tel"
                      value={editEmployee.phone || ''}
                      onChange={(e) => handleEditInputChange('phone', e.target.value)}
                      className={clsx(
                        "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                      )}
                      placeholder="Enter phone number (optional)"
                    />
                  </div>
                  <div>
                    <label className={clsx(
                      "block text-sm font-medium mb-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-700"
                    )}>Role *</label>
                    <select
                      required
                      value={editEmployee.role || ''}
                      onChange={(e) => handleEditInputChange('role', e.target.value)}
                      className={clsx(
                        "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white focus:bg-white/20"
                          : "bg-white border-gray-300 text-gray-900 focus:bg-white"
                      )}
                    >
                      {roleOptions.map((role) => (
                        <option key={role.value} value={role.value} className="bg-gray-800">
                          {role.label}
                        </option>
                      ))}
                    </select>
                    
                    {/* Role Description */}
                    <div className={clsx(
                      "mt-2 p-3 rounded-lg border transition-colors",
                      theme === 'dark'
                        ? "bg-white/5 border-white/10"
                        : "bg-gray-50 border-gray-200"
                    )}>
                      <div className="flex items-center mb-1">
                        <span className={clsx(
                          "font-medium transition-colors",
                          roleOptions.find(r => r.value === editEmployee.role)?.color || (theme === 'dark' ? 'text-gray-300' : 'text-gray-700')
                        )}>
                          {roleOptions.find(r => r.value === editEmployee.role)?.label || 'Select a role'}
                        </span>
                      </div>
                      <p className={clsx(
                        "text-sm transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>
                        {roleOptions.find(r => r.value === editEmployee.role)?.description || 'Please select a role to see details'}
                      </p>
                      
                      {/* Permissions List */}
                      {editEmployee.role && (
                        <div className="mt-2">
                          <p className={clsx(
                            "text-xs mb-1 transition-colors",
                            theme === 'dark' ? "text-gray-400" : "text-gray-500"
                          )}>Permissions:</p>
                          <div className="flex flex-wrap gap-1">
                            {roleOptions.find(r => r.value === editEmployee.role)?.permissions.map((permission, index) => (
                              <span 
                                key={index}
                                className={clsx(
                              "text-xs bg-indigo-500/20 px-2 py-1 rounded",
                              theme === 'dark' ? "text-indigo-300" : "text-indigo-700"
                            )}
                              >
                                {permission.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>Bio</label>
                  <textarea
                    value={editEmployee.bio || ''}
                    onChange={(e) => handleEditInputChange('bio', e.target.value)}
                    rows={3}
                    className={clsx(
                      "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                    )}
                    placeholder="Enter employee bio (optional)"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className={clsx(
                      "px-6 py-3 backdrop-blur-sm border font-medium rounded-xl transition-all duration-200",
                      theme === 'dark'
                        ? "bg-white/10 hover:bg-white/20 text-white border-white/20"
                        : "bg-white hover:bg-gray-50 text-gray-900 border-gray-300"
                    )}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isEditing}
                    className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed shadow-lg"
                  >
                    {isEditing ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Updating...
                      </div>
                    ) : (
                      'Update Employee'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagementPage; 